import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { AlertTriangle, CalendarPlus, Loader2, Send } from "lucide-react";

const fmt = (d) => { try { return new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return d; } };

function guestOptions(txn) {
  const buyerSide = txn.transaction_side === "buyer";
  const opts = buyerSide
    ? [["buyer", "Buyer"], ["buyer2", "Buyer 2"], ["seller_agent", "Seller's Agent"]]
    : [["seller", "Seller"], ["seller2", "Seller 2"], ["buyer_agent", "Buyer's Agent"]];
  return [...opts, ["lender", "Lender"], ["loan_processor", "Loan Processor"], ["agent_assistant", "Agent Assistant"]]
    .map(([k, role]) => ({ k, role, name: txn[`${k}_name`], email: (txn[`${k}_email`] || "").trim() }))
    .filter((g) => g.email.includes("@"));
}

export const inviteOutdated = (txn, k) => {
  const sent = txn.calendar_invites?.[k];
  const cur = String(txn[k] || "").trim().slice(0, 10);
  return !!(sent && cur && sent.date !== cur);
};

export function CalendarInviteButton({ dateKey, txn, onClick }) {
  if (!String(txn[dateKey] || "").trim()) return null;
  const sent = txn.calendar_invites?.[dateKey];
  if (inviteOutdated(txn, dateKey)) {
    return (
      <button type="button" data-testid={`invite-resend-btn-${dateKey}`} onClick={onClick} title={`Invite was sent for ${fmt(sent.date)}. The date has changed since.`}
        className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border border-[#F7D9A8] text-[#9a5b12] bg-[#FFF4E5] hover:bg-[#FBE7C6] transition-colors">
        <AlertTriangle className="w-3 h-3" /> Date changed · Resend
      </button>
    );
  }
  return (
    <button type="button" data-testid={`invite-btn-${dateKey}`} onClick={onClick} title={sent ? `Invite sent ${new Date(sent.sent_at).toLocaleString()}` : "Send calendar invite"}
      className={`mt-1 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border transition-colors ${sent ? "border-[#BFE3CC] text-[#1F8A4C] bg-[#EEF8F1] hover:bg-[#DFF1E5]" : "border-[#E5E5E2] text-[#1F3C88] hover:bg-[#F0EFEC]"}`}>
      <CalendarPlus className="w-3 h-3" /> {sent ? "Invite sent" : "Invite"}
    </button>
  );
}

export function CalendarInviteDialog({ txn, dateKey, label, sender, onClose, onSent }) {
  const [guests, setGuests] = useState([]);
  const [sending, setSending] = useState(false);
  const [title, setTitle] = useState("");
  const [day, setDay] = useState("");
  const [note, setNote] = useState("");
  const [picked, setPicked] = useState([]);
  const filledKeys = Object.keys(KEY_LABELS).filter((k) => /^\d{4}-\d{2}-\d{2}/.test(String(txn[k] || "").trim()));
  useEffect(() => { setGuests([]); setTitle(""); setDay(""); setNote(""); setPicked(dateKey === "all" ? filledKeys : []); }, [dateKey]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!dateKey) return null;
  const custom = dateKey === "custom";
  const all = dateKey === "all";
  const side = txn.transaction_side;
  const sideOk = side === "buyer" || side === "seller";
  const agentKey = side === "buyer" ? "buyer_agent" : "seller_agent";
  const agentEmail = sideOk ? (txn[`${agentKey}_email`] || "").trim() : "";
  const fromName = txn.coordinator_name || sender?.name || "You";
  const fromEmail = (txn.coordinator_email || "").trim() || sender?.email;
  const opts = guestOptions(txn);
  const sent = custom || all ? null : txn.calendar_invites?.[dateKey];
  const canSend = agentEmail && (!custom || (title.trim() && day)) && (!all || picked.length);
  const toggle = (k) => setGuests((g) => (g.includes(k) ? g.filter((x) => x !== k) : [...g, k]));
  const togglePick = (k) => setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const sendAll = async () => {
    let ok = 0; const failed = [];
    for (const k of filledKeys.filter((x) => picked.includes(x))) {
      try { await api.post(`/tm/transactions/${txn.transaction_id}/calendar-invite`, { date_key: k, guests }); ok += 1; }
      catch (err) { failed.push(`${KEY_LABELS[k]}: ${formatApiError(err.response?.data?.detail)}`); }
    }
    if (ok) toast.success(`Sent ${ok} calendar invite${ok === 1 ? "" : "s"}`);
    failed.forEach((f) => toast.error(f));
    onSent();
    if (!failed.length) onClose();
  };
  const send = async () => {
    setSending(true);
    if (all) { try { await sendAll(); } finally { setSending(false); } return; }
    try {
      await api.post(`/tm/transactions/${txn.transaction_id}/calendar-invite`, custom ? { date_key: "custom", title, date: day, note, guests } : { date_key: dateKey, guests });
      toast.success(`Calendar invite sent for ${custom ? title.trim() : label}`);
      onSent(); onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="calendar-invite-dialog">
        <h3 className="font-heading text-lg font-bold text-[#0A0A0A] flex items-center gap-2"><CalendarPlus className="w-5 h-5 text-[#F7941D]" /> Send Calendar Invite</h3>
        {custom ? (
          <div className="space-y-2" data-testid="invite-custom-fields">
            <label className="block"><span className={lblCls}>Event title</span>
              <input data-testid="invite-custom-title" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Final walkthrough" className={inpCls} /></label>
            <label className="block"><span className={lblCls}>Date (all day)</span>
              <input type="date" data-testid="invite-custom-date" value={day} onChange={(e) => setDay(e.target.value)} className={inpCls} /></label>
            <label className="block"><span className={lblCls}>Note (optional)</span>
              <textarea data-testid="invite-custom-note" rows={2} value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} className={inpCls} /></label>
          </div>
        ) : all ? (
          <div data-testid="invite-all-dates">
            <span className={lblCls}>Dates to send (each becomes its own all-day event)</span>
            {filledKeys.length ? filledKeys.map((k) => {
              const s = txn.calendar_invites?.[k];
              const tag = !s ? null : inviteOutdated(txn, k) ? ["Date changed", "bg-[#FFF4E5] text-[#9a5b12]"] : ["Already sent · sends update", "bg-[#EEF8F1] text-[#1F8A4C]"];
              return (
                <label key={k} className="flex items-center gap-2 py-1 text-sm cursor-pointer" data-testid={`invite-all-pick-${k}`}>
                  <input type="checkbox" checked={picked.includes(k)} onChange={() => togglePick(k)} className="accent-[#1F3C88]" />
                  <span className="font-medium">{KEY_LABELS[k]}</span><span className="text-[#8a8a86]">{fmt(txn[k])}</span>
                  {tag && <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${tag[1]}`}>{tag[0]}</span>}
                </label>
              );
            }) : <p className="text-xs text-[#8a8a86]" data-testid="invite-all-none">No key dates are filled in yet, so there's nothing to send.</p>}
          </div>
        ) : <p className="text-sm text-[#575755]" data-testid="invite-summary"><strong>{label}</strong> · {fmt(txn[dateKey])} · all day</p>}
        <div className="text-sm space-y-1">
          <div><span className={lblCls}>From</span><span data-testid="invite-from">{fromName}{fromEmail ? ` <${fromEmail}>` : ""}</span></div>
          <div><span className={lblCls}>To (our {side === "buyer" ? "buyer's" : "seller's"} agent)</span>
            {agentEmail ? <span data-testid="invite-agent">{txn[`${agentKey}_name`] || agentEmail} &lt;{agentEmail}&gt;</span>
              : <span data-testid="invite-agent-missing" className="text-[#B42318]">{sideOk ? `Add the ${side === "buyer" ? "buyer's" : "seller's"} agent email in People first.` : "Set this file to Buyer or Seller side first."}</span>}
          </div>
        </div>
        <div>
          <span className={lblCls}>Optional guests</span>
          {opts.length ? opts.map((g) => (
            <label key={g.k} className="flex items-center gap-2 py-1 text-sm cursor-pointer" data-testid={`invite-guest-${g.k}`}>
              <input type="checkbox" checked={guests.includes(g.k)} onChange={() => toggle(g.k)} className="accent-[#1F3C88]" />
              <span className="font-medium">{g.role}</span><span className="text-[#8a8a86] truncate">{g.name ? `${g.name} · ` : ""}{g.email}</span>
            </label>
          )) : <p className="text-xs text-[#8a8a86]">No other people on this file have an email yet.</p>}
        </div>
        {sent && inviteOutdated(txn, dateKey) && <p data-testid="invite-date-changed" className="text-xs text-[#9a5b12] bg-[#FFF4E5] border border-[#F7D9A8] rounded-sm px-2.5 py-1.5">The invite sent {new Date(sent.sent_at).toLocaleDateString()} was for {fmt(sent.date)}. Resend so everyone's calendar moves to {fmt(txn[dateKey])}.</p>}
        {sent && !inviteOutdated(txn, dateKey) && <p data-testid="invite-last-sent" className="text-xs text-[#575755] bg-[#F7F6F3] rounded-sm px-2.5 py-1.5">Last sent {new Date(sent.sent_at).toLocaleString()} for {fmt(sent.date)}. Sending again updates the same calendar event.</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} data-testid="invite-cancel" className="px-4 py-2 rounded-full text-sm border border-[#E5E5E2] hover:bg-[#F0EFEC]">Cancel</button>
          <button onClick={send} disabled={sending || !canSend} data-testid="invite-send" className="px-4 py-2 rounded-full text-sm font-semibold bg-[#1F3C88] text-white hover:bg-[#162D6A] disabled:opacity-50 inline-flex items-center gap-1.5">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {all ? `Send ${picked.length} invite${picked.length === 1 ? "" : "s"}` : "Send invite"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const lblCls = "text-[11px] tracking-[0.1em] uppercase text-[#8a8a86] block mb-0.5";

const KEY_LABELS = { contract_date: "Contract Date", closing_date: "Closing", earnest_money_deadline: "Earnest Money Due", inspection_deadline: "Inspection Deadline",
  financing_deadline: "Financing Deadline", appraisal_deadline: "Appraisal Deadline", insurance_deadline: "Insurance Deadline", possession_date: "Possession" };

function inviteRows(txn) {
  const rows = [...(txn.invite_history || [])];
  const seen = new Set(rows.map((r) => r.sent_at));
  (txn.custom_invites || []).forEach((r) => { if (!seen.has(r.sent_at)) rows.push({ ...r, kind: "custom" }); });
  Object.entries(txn.calendar_invites || {}).forEach(([k, r]) => { if (!seen.has(r.sent_at)) rows.push({ ...r, kind: k, title: KEY_LABELS[k] }); });
  return rows.sort((a, b) => String(b.sent_at).localeCompare(String(a.sent_at)));
}

export function InviteHistory({ txn }) {
  const [open, setOpen] = useState(false);
  const rows = inviteRows(txn);
  if (!rows.length) return null;
  return (
    <div className="mt-4 border-t border-[#F0EFEC] pt-3" data-testid="invite-history">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="invite-history-toggle" className="text-xs font-semibold text-[#1F3C88] hover:underline">
        {open ? "Hide" : "Show"} invite history ({rows.length})
      </button>
      {open && (
        <>
        <div className="mt-2 space-y-2 sm:hidden">
          {rows.map((r, i) => (
            <div key={`m-${r.sent_at}-${i}`} data-testid={`invite-history-card-${i}`} className="border border-[#F0EFEC] rounded-sm p-2.5 text-sm">
              <div className="font-medium text-[#0A0A0A]">{r.title || KEY_LABELS[r.kind] || "Invite"} · {fmt(r.date)}{r.sequence > 0 && <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[#EEF2FB] text-[#1F3C88]">Update</span>}</div>
              <div className="text-xs text-[#8a8a86] mt-0.5">Sent {new Date(r.sent_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} by {r.by}</div>
              <div className="text-xs text-[#575755] mt-1 break-all">{[r.to, ...(r.guests || [])].filter(Boolean).join(", ")}</div>
            </div>
          ))}
        </div>
        <div className="mt-2 overflow-x-auto hidden sm:block">
          <table className="w-full text-left text-sm border-collapse">
            <thead><tr className="text-[11px] tracking-[0.1em] uppercase text-[#8a8a86]">
              <th className="py-1.5 pr-4 font-semibold">Sent</th><th className="py-1.5 pr-4 font-semibold">Event</th><th className="py-1.5 pr-4 font-semibold">Event date</th><th className="py-1.5 font-semibold">Recipients</th>
            </tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.sent_at}-${i}`} data-testid={`invite-history-row-${i}`} className="border-t border-[#F0EFEC] align-top">
                  <td className="py-2 pr-4 whitespace-nowrap text-[#575755]">{new Date(r.sent_at).toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}<div className="text-[11px] text-[#8a8a86]">{r.by}</div></td>
                  <td className="py-2 pr-4 font-medium text-[#0A0A0A]">{r.title || KEY_LABELS[r.kind] || "Invite"}{r.sequence > 0 && <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[#EEF2FB] text-[#1F3C88]">Update</span>}</td>
                  <td className="py-2 pr-4 whitespace-nowrap">{fmt(r.date)}</td>
                  <td className="py-2 text-[#575755] break-all">{[r.to, ...(r.guests || [])].filter(Boolean).join(", ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}
const inpCls = "w-full px-3 py-2 bg-white border border-[#E5E5E2] rounded-sm text-sm focus:ring-2 focus:ring-[#F7941D] focus:outline-none";
